import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  model,
  resource,
  signal,
} from '@angular/core';
import { Skin } from '@core/interfaces/skin.interface';
import { ModalService } from '@features/modals/services/modal.service';
import { InputOptionsComponent } from '@core/components/input-options/input-options.component';
import { SearchInputComponent } from '@core/components/search-input/search-input.component';
import { SelectComponent } from '@core/components/select/select.component';
import { SkinsListComponent } from '@core/components/skins-list/skins-list.component';
import { CostComponent } from '@core/components/cost/cost.component';
import { AdminCaseCreatorService } from '../admin-case-creator.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { AdminCaseEditService } from '../admin-case-edit/admin-case-edit.service';
import { sleep } from '@core/utils/sleep.util';

enum SortOptions {
  highest_price,
  lowest_price,
}

interface SortOption {
  label: string;
  key: keyof typeof SortOptions;
  fn: () => void;
}

@Component({
  selector: 'app-admin-case-add-item-modal',
  imports: [
    InputOptionsComponent,
    SvgIconComponent,
    SearchInputComponent,
    SelectComponent,
    SkinsListComponent,
    CostComponent,
  ],
  templateUrl: './admin-case-add-item-modal.component.html',
  styleUrl: './admin-case-add-item-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    class: 'modal-container flex flex-col',
  },
})
export class AdminCaseAddItemModalComponent {
  private readonly modalService = inject(ModalService);
  private readonly adminCaseCreatorService = inject(AdminCaseCreatorService);
  public readonly adminCaseEditService = inject(AdminCaseEditService);

  public readonly adminAllSkinsResource = resource({
    loader: async () => {
      const skins = await this.adminCaseCreatorService.loadAdminSkins();
      const finalSkins =
        this.adminCaseCreatorService.getSkinsByAdminAllSkinsSkin(skins);
      return finalSkins;
    },
  });

  readonly sortOptions: SortOption[] = [
    {
      label: 'Highest Price',
      key: 'highest_price',
      fn: () => {
        this.sort.set(this.sortOptions[0]);
      },
    },
    {
      label: 'Lowest Price',
      key: 'lowest_price',
      fn: () => {
        this.sort.set(this.sortOptions[1]);
      },
    },
  ];
  readonly sort = signal<SortOption>(this.sortOptions[0]);

  skins = computed<Skin[]>(() => {
    if (!this.adminAllSkinsResource.hasValue()) {
      return [];
    }

    return (
      this.adminAllSkinsResource
        .value()
        .filter((skin) => {
          const filteredBySearch = skin.name
            ?.toLowerCase()
            .includes(this.search().toLowerCase() ?? '');

          const matchesPrice =
            (this.fromPrice() === 0 || skin.price >= this.fromPrice()) &&
            (this.toPrice() === 0 || skin.price <= this.toPrice());
          return filteredBySearch && matchesPrice;
        })
        .sort((a, b) => {
          if (this.sort().key === 'highest_price') {
            return b.price - a.price;
          }
          return a.price - b.price;
        }) ?? []
    );
  });
  matchedSkins = computed<Skin[]>(() => {
    const selectedAssetIds = this.adminCaseEditService
      .caseSkins()
      .map((skin) => skin.assetid);

    return (
      this.adminAllSkinsResource
        .value()
        ?.filter((skin) => selectedAssetIds.includes(skin.assetid)) ?? []
    );
  });

  readonly selectedSkinsPrice = computed(() => {
    return this.matchedSkins().reduce((acc, skin) => acc + skin.price, 0);
  });

  readonly search = signal<string>('');

  readonly toPrice = signal<number>(100);
  readonly fromPrice = signal<number>(0);

  public isShowRefreshAnimation = signal(false);

  constructor() {
    const initPriceRange = effect(() => {
      this.fromPrice.set(
        Math.min(
          ...this.adminCaseEditService.caseSkins().map((skin) => skin.price)
        )
      );
      this.toPrice.set(
        Math.max(
          ...this.adminCaseEditService.caseSkins().map((skin) => skin.price)
        )
      );
      initPriceRange.destroy();
    });

    const initPriceRangeAfterSkinsLoaded = effect(() => {
      if (this.adminAllSkinsResource.hasValue()) {
        const selectedAssetIds = this.adminCaseEditService
          .caseSkins()
          .map((skin) => skin.assetid);

        const matchingSkinsInAdmin = this.adminAllSkinsResource
          .value()
          .filter((skin) => selectedAssetIds.includes(skin.assetid));

        const maxPrice = Math.max(
          ...matchingSkinsInAdmin.map((skin) => skin.price)
        );

        const minPrice = Math.min(
          ...matchingSkinsInAdmin.map((skin) => skin.price)
        );

        this.toPrice.set(maxPrice);
        this.fromPrice.set(minPrice);
        initPriceRangeAfterSkinsLoaded.destroy();
      }
    });
  }

  public closeModal() {
    this.modalService.close();
  }

  public async refreshInventory() {
    this.isShowRefreshAnimation.set(true);
    this.adminAllSkinsResource.reload();
    await sleep(500);
    this.isShowRefreshAnimation.set(false);
  }
}
